import { queryPipelineRunSummary } from '@/features/pipelines/data';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { buildBuildPipelineFilters } from './pipelinePageFilters';
import { toPipelineRunDisplayStatusFilters } from './pipelineStatusScope';
import type { PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type {
  BuildStatus,
  PipelineLatestRunDto,
  PipelineListItemDto,
  PipelineDailyStatusDto,
  PipelineRunSummaryItemDto,
  QueryResult,
} from '@flowqa/shared';

const buildEmptyBuildStatusCounts = (): Record<BuildStatus, number> => ({
  queued: 0,
  running: 0,
  completed: 0,
  failed: 0,
});

const comparePipelineRunSummariesDesc = (
  left: PipelineRunSummaryItemDto,
  right: PipelineRunSummaryItemDto,
): number => {
  const leftTimestamp = left.timestamp?.getTime() ?? Number.MIN_SAFE_INTEGER;
  const rightTimestamp = right.timestamp?.getTime() ?? Number.MIN_SAFE_INTEGER;

  if (leftTimestamp !== rightTimestamp) {
    return rightTimestamp - leftTimestamp;
  }

  return right.id.localeCompare(left.id);
};

const buildLatestRunFromSummary = (
  runSummary: PipelineRunSummaryItemDto | undefined,
): PipelineLatestRunDto | undefined => {
  if (!runSummary?.timestamp) {
    return undefined;
  }

  return {
    id: runSummary.id,
    buildKey: runSummary.buildKey,
    status: runSummary.status,
    lifecycleState: runSummary.lifecycleState,
    conclusion: runSummary.conclusion,
    timestamp: runSummary.timestamp,
    durationMs: runSummary.durationMs,
  };
};

const buildRunStatusCounts = (
  runSummaries: readonly PipelineRunSummaryItemDto[],
): Record<BuildStatus, number> =>
  runSummaries.reduce<Record<BuildStatus, number>>((counts, runSummary) => {
    counts[runSummary.status] += 1;
    return counts;
  }, buildEmptyBuildStatusCounts());

const buildPipelineDailyHistory = (
  runSummaries: readonly PipelineRunSummaryItemDto[],
): PipelineDailyStatusDto[] => {
  const historyByDay = new Map<string, PipelineDailyStatusDto>();

  runSummaries.forEach((runSummary) => {
    if (!(runSummary.timestamp instanceof Date)) {
      return;
    }

    const dayKey = runSummary.timestamp.toISOString().slice(0, 10);
    const existing = historyByDay.get(dayKey) ?? {
      day: new Date(`${dayKey}T00:00:00.000Z`),
      status: null,
      statuses: [],
      details: [],
    };

    existing.statuses.push(runSummary.status);
    existing.details.push({
      status: runSummary.status,
      timestamp: runSummary.timestamp,
      runLabel: runSummary.buildKey ?? null,
    });
    existing.details.sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime());
    existing.status = existing.details[0]?.status ?? null;
    historyByDay.set(dayKey, existing);
  });

  return Array.from(historyByDay.values()).sort(
    (left, right) => right.day.getTime() - left.day.getTime(),
  );
};

export const groupPipelineRunSummariesByPipelineId = (
  runSummaries: readonly PipelineRunSummaryItemDto[],
): Map<string, PipelineRunSummaryItemDto[]> => {
  const grouped = new Map<string, PipelineRunSummaryItemDto[]>();

  runSummaries.forEach((runSummary) => {
    const existing = grouped.get(runSummary.pipelineId) ?? [];
    existing.push(runSummary);
    grouped.set(runSummary.pipelineId, existing);
  });

  grouped.forEach((pipelineRunSummaries, pipelineId) => {
    grouped.set(pipelineId, [...pipelineRunSummaries].sort(comparePipelineRunSummariesDesc));
  });

  return grouped;
};

export const applyFilteredRunSummariesToPipelines = (
  pipelines: readonly PipelineListItemDto[],
  runSummariesByPipelineId: ReadonlyMap<string, readonly PipelineRunSummaryItemDto[]>,
): PipelineListItemDto[] =>
  pipelines.map((pipeline) => {
    const pipelineRunSummaries = runSummariesByPipelineId.get(pipeline.id);
    if (!pipelineRunSummaries || pipelineRunSummaries.length === 0) {
      return pipeline;
    }

    const sortedRunSummaries = [...pipelineRunSummaries].sort(comparePipelineRunSummariesDesc);
    const latestRunSummary = sortedRunSummaries[0];

    return {
      ...pipeline,
      latestRun: buildLatestRunFromSummary(latestRunSummary),
      runSummaries: sortedRunSummaries,
      buildTotal: sortedRunSummaries.length,
      buildStatusCounts: buildRunStatusCounts(sortedRunSummaries),
      stageTotal: latestRunSummary.stageTotal ?? 0,
      stageStatusCounts: latestRunSummary.stageStatusCounts ?? buildEmptyBuildStatusCounts(),
      stageSummaries: latestRunSummary.stageSummaries ?? [],
      latestJobTotal: latestRunSummary.jobTotal ?? 0,
      latestJobStatusCounts: latestRunSummary.jobStatusCounts ?? buildEmptyBuildStatusCounts(),
      latestJobSummaries: latestRunSummary.jobSummaries ?? [],
      dailyHistory: buildPipelineDailyHistory(sortedRunSummaries),
    };
  });

type FetchFilteredPipelineRunSummariesArgs = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  pipelineIds: string[];
  statuses: PipelineStatusKey[];
  selectedTags?: string[];
  startDay: string | null;
  endDay: string | null;
};

export const fetchFilteredPipelineRunSummaries = async ({
  selectedProjectId,
  selectedOrganisationId,
  pipelineIds,
  statuses,
  selectedTags = [],
  startDay,
  endDay,
}: FetchFilteredPipelineRunSummariesArgs): Promise<PipelineRunSummaryItemDto[]> => {
  if (pipelineIds.length === 0 || statuses.length === 0) {
    return [];
  }

  const queryResult = await fetchAllQueryPages<PipelineRunSummaryItemDto>(
    async ({ page, pageSize }): Promise<QueryResult<PipelineRunSummaryItemDto>> => {
      const result = await queryPipelineRunSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page,
          pageSize,
          sorts: [{ field: 'startedAt', direction: 'desc' }],
          filters: mergeFilters(
            buildBuildPipelineFilters(pipelineIds),
            buildInFilter('displayStatus', toPipelineRunDisplayStatusFilters(statuses)),
            buildInFilter('tags', selectedTags),
          ),
          metricsStartDay: startDay ?? undefined,
          metricsEndDay: endDay ?? undefined,
          latestOnly: false,
        },
      );

      return {
        items: result.runSummaries,
        total: result.buildTotal,
        page,
        pageSize,
      };
    },
  );

  return queryResult.items;
};
